// Pagination コンポーネント - UI/UX設計仕様書準拠

import React from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  FirstPage,
  LastPage,
} from '@mui/icons-material';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showInfo?: boolean;
  totalItems?: number;
  itemsPerPage?: number;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  showFirstLast?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  showInfo = false,
  totalItems,
  itemsPerPage = 20,
  disabled = false,
  size = 'medium',
  showFirstLast = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Calculate visible page range
  const getVisiblePages = () => {
    const maxVisible = isMobile ? 5 : 7;
    const halfVisible = Math.floor(maxVisible / 2);

    let start = Math.max(1, currentPage - halfVisible);
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();

  // Calculate info text
  const getInfoText = () => {
    if (!showInfo || !totalItems) return '';

    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalItems);

    return `${start}-${end} / ${totalItems}件`;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage && !disabled) {
      onPageChange(page);
    }
  };

  if (totalPages <= 1) {
    return showInfo ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {getInfoText()}
        </Typography>
      </Box>
    ) : null;
  }

  const buttonSize = size === 'small' ? 'small' : 'medium';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        p: 2,
      }}
    >
      {/* Info text */}
      {showInfo && (
        <Typography variant="body2" color="text.secondary">
          {getInfoText()}
        </Typography>
      )}

      {/* Pagination controls */}
      <Stack direction="row" spacing={1} alignItems="center">
        {/* First page button */}
        {showFirstLast && !isMobile && (
          <IconButton
            size={buttonSize}
            onClick={() => handlePageChange(1)}
            disabled={disabled || currentPage === 1}
            aria-label="最初のページ"
          >
            <FirstPage />
          </IconButton>
        )}

        {/* Previous page button */}
        <IconButton
          size={buttonSize}
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={disabled || currentPage === 1}
          aria-label="前のページ"
        >
          <ChevronLeft />
        </IconButton>

        {/* Page number buttons */}
        {visiblePages.map((page) => (
          <Button
            key={page}
            variant={page === currentPage ? 'contained' : 'outlined'}
            size={buttonSize}
            onClick={() => handlePageChange(page)}
            disabled={disabled}
            sx={{
              minWidth: size === 'small' ? 32 : 40,
              height: size === 'small' ? 32 : 40,
              color:
                page === currentPage ? 'primary.contrastText' : 'primary.main',
              backgroundColor:
                page === currentPage ? 'primary.main' : 'transparent',
              borderColor: 'primary.main',
              '&:hover': {
                backgroundColor:
                  page === currentPage ? 'primary.dark' : 'primary.light',
                borderColor: 'primary.main',
              },
              '&.Mui-disabled': {
                borderColor: 'action.disabled',
              },
            }}
          >
            {page}
          </Button>
        ))}

        {/* Next page button */}
        <IconButton
          size={buttonSize}
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={disabled || currentPage === totalPages}
          aria-label="次のページ"
        >
          <ChevronRight />
        </IconButton>

        {/* Last page button */}
        {showFirstLast && !isMobile && (
          <IconButton
            size={buttonSize}
            onClick={() => handlePageChange(totalPages)}
            disabled={disabled || currentPage === totalPages}
            aria-label="最後のページ"
          >
            <LastPage />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
};

export default Pagination;

